import React, { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import FLFDeviceControlPanel from "../../components/ControlPanel/FLFDeviceControlPanel";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import DevicePanelOptional from "../../components/ControlPanel/PanelOptional/DevicePanelOptional";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import DeviceTable from "../../components/Table/DeviceTable";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import useQueryConfig from "src/hooks/useQueryConfig";
import { useGetPermissionAccessControl } from "src/context/PermissionAccessControl";
import {
  getFLFDevice,
  removeFLFDevice,
  sortFLFDevice,
  updateFLFDevice,
} from "src/apis/FLF/device.api";
import { NotifyConfirm, Toast } from "src/components/notify/Notify";
import { CACHE_DEVICE } from "src/constants/actionCache";
import { compareTwoArrayDnD } from "src/utils/helper";
import { path } from "src/constants/path";

const initDeviceData = { list: [], total_record: 0 };

const FLFDevice = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();

  // cache
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  let queryConfig = useQueryConfig();
  const { categoryId: categoryIdParam, iotPartnerId: iotPartnerIdParam } =
    queryConfig;

  // const [listDevice, setListDevice] = useState(initDevices);
  const [deviceData, setDeviceData] = useState(initDeviceData);
  const [listDeviceId, setListDeviceId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [filterDevices, setFilterDevices] = useState([]);
  const [iotPartnerId, setIotPartnerId] = useState(
    iotPartnerIdParam ? iotPartnerIdParam : "all"
  );
  const [categoryId, setCategoryId] = useState(
    categoryIdParam ? categoryIdParam : "all"
  );
  const permissionsAccessControl = useGetPermissionAccessControl();

  const fetchDevices = useCallback(() => {
    getFLFDevice({ page }, (result) => {
      setDeviceData({ ...result });
      setFilterDevices([...result.list]);
      setListDeviceId([...result.list?.map((d) => d.id)]);
    });
  }, [page]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredDevices = useCallback((devices) => {
    setFilterDevices(devices);
    setListDeviceId(devices.map((device) => device.id));
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (devices, key) => {
      const search = searchKey.toLowerCase().trim();
      return devices.filter((device) => {
        const deviceName = (device.name || "").toString().toLowerCase();
        const categoryName = (device?.category?.name || "")
          .toString()
          .toLowerCase();

        return (
          deviceName.indexOf(search) !== -1 ||
          categoryName.indexOf(search) !== -1
        );
      });
    },
    [searchKey]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredDevices = value
        ? searchByKeyWord(deviceData.list, value)
        : deviceData.list;
      updateFilteredDevices(filteredDevices);
    },
    [deviceData.list, searchByKeyWord, updateFilteredDevices]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle sort and DnD ----- */
  const handleSort = useCallback(() => {
    sortFLFDevice({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_DEVICE);
      fetchDevices();
    });
  }, [fetchDevices, queueSortApi, setCurrentActionCache]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterDevices.findIndex(
        (device) => device.id === active.id
      );
      const activeDevice = filterDevices.find((c) => c.id === active.id);

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterDevices.findIndex(
        (device) => device.id === over.id
      );
      const overDevice = filterDevices.find((c) => c.id === over.id);

      if (activeDevice.categoryId === overDevice.categoryId) {
        const newOrder = arrayMove(filterDevices, oldIndex, newIndex);
        const newOrderIds = newOrder.map((c) => c.id);

        const isOrderChanged = !compareTwoArrayDnD(listDeviceId, newOrderIds);
        if (isOrderChanged) {
          setIsSorted(true);
          setQueueSortApi((prev) => [
            ...prev,
            {
              activeDeviceId: active.id,
              overDeviceId: over.id,
              categoryId: activeDevice.categoryId,
            },
          ]);
        } else {
          setIsSorted(false);
          setQueueSortApi([]);
        }

        setFilterDevices([...newOrder]);
      } else {
        Toast("warning", "2 thiết bị không cùng category");
      }
    }
  };

  const handleDelete = (id) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFDevice(id, () => {
          setCurrentActionCache(CACHE_DEVICE);
          fetchDevices();
        });
      }
    });
  };

  const handleShowHomePage = (item) => (event) => {
    const { value } = event.target;

    NotifyConfirm("warning", "Are you change it ?", (result) => {
      if (result.isConfirmed) {
        const foundDeviceIndex = deviceData.list.findIndex(
          (device) => device.id === item.id
        );
        const isShowHomePageUpdate = value === "show" ? true : false;

        const rogoDevSubTypeIds = item.rogoDevSubTypes.map(
          (rogoDev) => rogoDev.id
        );

        const pairingTypeIds = item.pairingTypes.map(
          (pairingType) => pairingType.id
        );

        if (foundDeviceIndex !== -1) {
          // call api update
          updateFLFDevice(
            item.id,
            {
              ...item,
              isShowHomePage: isShowHomePageUpdate,
              rogoDevSubTypeIds,
              pairingTypeIds,
            },
            (result) => {
              setCurrentActionCache(CACHE_DEVICE);
              // callback nhận về lỗi form và thành công
              if (result.data?.status === 422) {
                Toast("warning", "Something went wrong !");
              } else {
                fetchDevices();
              }
            }
          );
        }
      }
    });
  };

  const handleChangeIotPartner = (value) => {
    setIotPartnerId(value);
    setNumberPage(1);
  };

  const handleChangeCategoryId = (value) => {
    setCategoryId(value);
    setNumberPage(1);
  };

  /* ---- result for render ----- */
  const returnListDeviceFilterByIotPartnerId = useCallback(
    (devices) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      if (iotPartnerId === "all") {
        return devices;
      } else {
        return devices.filter((device) => device.iotPartnerId === iotPartnerId);
      }
    },
    [iotPartnerId, status]
  );

  const returnListDeviceFilterByCategoryId = useCallback(
    (devices) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      if (categoryId === "all") {
        return devices;
      } else {
        return devices.filter((device) => device.categoryId === categoryId);
      }
    },
    [categoryId, status]
  );

  const resultFilter = useMemo(() => {
    let deviceFilterByIotPartner =
      returnListDeviceFilterByIotPartnerId(filterDevices);
    let deviceFilterByCategory = returnListDeviceFilterByCategoryId(
      deviceFilterByIotPartner
    );

    return searchByKeyWord(deviceFilterByCategory, searchKey);
  }, [
    filterDevices,
    returnListDeviceFilterByCategoryId,
    returnListDeviceFilterByIotPartnerId,
    searchByKeyWord,
    searchKey,
  ]);

  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{"FLF Device"}</h2>
      </div>

      {/** control pan */}
      <FLFDeviceControlPanel
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        permissionsAccessControl={permissionsAccessControl}
        categoryId={categoryIdParam}
        page={numberPage}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />

        <DevicePanelOptional
          onChangeIotPartner={handleChangeIotPartner}
          iotPartnerId={iotPartnerId}
          onChangeCategory={handleChangeCategoryId}
          categoryId={categoryId}
        />
      </FLFDeviceControlPanel>

      <DeviceTable
        data={listItems}
        handleDelete={handleDelete}
        handleShowHomePage={handleShowHomePage}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      <Pagination
        pathname={path.flfDevice}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFDevice;
